import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Avatar,
  LinearProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  People,
  Store,
  AccountBalance,
  Assignment,
  Visibility,
  Phone,
  PersonAdd,
  LocationOn,
  Money,
  Receipt,
  ShoppingCart,
  Schedule,
  CalendarToday,
  AccessTime,
  Flag,
  Warning,
  PendingActions,
  EventBusy,
  Inventory
} from '@mui/icons-material';

// Retail Dashboard Stats
const retailDashboardStats = [
  {
    title: 'Total Sales',
    value: '₹12.8Cr',
    change: '+18.2%',
    trending: 'up',
    icon: Money,
    color: '#1976d2'
  },
  {
    title: 'Active Customers',
    value: '2,847',
    change: '+12.5%',
    trending: 'up',
    icon: People,
    color: '#388e3c'
  },
  {
    title: 'Products Sold',
    value: '8,456',
    change: '+8.7%',
    trending: 'up',
    icon: Store,
    color: '#f57c00'
  },
  {
    title: 'Revenue',
    value: '₹8.9Cr',
    change: '+15.3%',
    trending: 'up',
    icon: AccountBalance,
    color: '#7b1fa2'
  },
  {
    title: 'Store Locations',
    value: '24',
    change: '+3.0%',
    trending: 'up',
    icon: LocationOn,
    color: '#00796b'
  },
  {
    title: 'Online Orders',
    value: '1,567',
    change: '+22.1%',
    trending: 'up',
    icon: Assignment,
    color: '#5d4037'
  },
  {
    title: 'New Customers',
    value: '342',
    change: '+28.4%',
    trending: 'up',
    icon: PersonAdd,
    color: '#303f9f'
  },
  {
    title: 'Returns',
    value: '89',
    change: '-5.2%',
    trending: 'down',
    icon: Receipt,
    color: '#e64a19'
  }
];

// Today's Agenda data for Retail
const todaysAgenda = [
  {
    id: 1,
    type: 'delivery',
    title: 'Product Delivery - Downtown Store',
    customer: 'Rajesh Kumar',
    time: '10:00 AM',
    location: 'Store #12, Downtown Mall',
    phone: '+91 9876543210',
    status: 'Scheduled'
  },
  {
    id: 2,
    type: 'meeting',
    title: 'Supplier Meeting - Electronics',
    customer: 'Tech Solutions Ltd',
    time: '02:30 PM',
    location: 'Office - Conference Room 1',
    phone: '+91 9876543211',
    status: 'Confirmed'
  },
  {
    id: 3,
    type: 'followup-customer',
    title: 'Customer Follow-up Call',
    customer: 'Priya Sharma',
    time: '11:15 AM',
    location: 'Phone Call',
    phone: '+91 9876543212',
    status: 'Pending'
  },
  {
    id: 4,
    type: 'inventory',
    title: 'Inventory Check - Electronics',
    customer: 'Store Manager',
    time: '04:00 PM',
    location: 'Main Warehouse',
    phone: '+91 9876543213',
    status: 'High Priority'
  },
  {
    id: 5,
    type: 'delivery',
    title: 'Bulk Order Delivery',
    customer: 'Amit ',
    time: '09:30 AM',
    location: 'Corporate Office',
    phone: '+91 9876543214',
    status: 'In Progress'
  },
  {
    id: 6,
    type: 'training',
    title: 'Staff Training Session',
    customer: 'Store Team',
    time: '03:45 PM',
    location: 'Training Room',
    phone: '+91 9876543215',
    status: 'Scheduled'
  }
];

// Pending Tasks data for Retail
const pendingTasks = [
  {
    id: 1,
    type: 'pending-delivery',
    title: 'Delayed Product Delivery',
    customer: 'Ramesh Electronics',
    dueDate: '2025-10-08',
    priority: 'High',
    phone: '+91 9876543220',
    location: 'Store #5, Mall Road',
    daysOverdue: 2
  },
  {
    id: 2,
    type: 'followup',
    title: 'Customer Complaint Follow-up',
    customer: 'Sunita Verma',
    dueDate: '2025-10-09',
    priority: 'Medium',
    phone: '+91 9876543221',
    location: 'Phone Call',
    daysOverdue: 1
  },
  {
    id: 3,
    type: 'inventory-restock',
    title: 'Low Stock Alert - Smartphones',
    customer: 'Inventory Team',
    dueDate: '2025-10-07',
    priority: 'High',
    phone: '+91 9876543222',
    location: 'Main Store',
    daysOverdue: 3
  },
  {
    id: 4,
    type: 'pending-delivery',
    title: 'Custom Order Delivery',
    customer: 'Kavitha ',
    dueDate: '2025-10-09',
    priority: 'Medium',
    phone: '+91 9876543223',
    location: 'Corporate Office',
    daysOverdue: 1
  },
  {
    id: 5,
    type: 'followup',
    title: 'Payment Follow-up Required',
    customer: 'Dinesh Trading Co',
    dueDate: '2025-10-06',
    priority: 'High',
    phone: '+91 9876543224',
    location: 'Phone Call',
    daysOverdue: 4
  },
  {
    id: 6,
    type: 'inventory-restock',
    title: 'Seasonal Stock Preparation',
    customer: 'Store Manager',
    dueDate: '2025-10-08',
    priority: 'Medium',
    phone: '+91 9876543225',
    location: 'All Stores',
    daysOverdue: 2
  }
];

const recentOrders = [
  {
    id: 'ORD001',
    customerName: 'Rajesh Kumar',
    items: '5 Items',
    amount: 125000,
    status: 'Delivered',
    date: '2025-10-08',
    phone: '+91 9876543210'
  },
  {
    id: 'ORD002',
    customerName: 'Priya Sharma',
    items: '3 Items',
    amount: 89000,
    status: 'Processing',
    date: '2025-10-07',
    phone: '+91 9876543211'
  },
  {
    id: 'ORD003',
    customerName: 'Amit Patel',
    items: '8 Items',
    amount: 245000,
    status: 'Shipped',
    date: '2025-10-06',
    phone: '+91 9876543212'
  },
  {
    id: 'ORD004',
    customerName: 'Sneha Reddy',
    items: '2 Items',
    amount: 65000,
    status: 'Pending',
    date: '2025-10-05',
    phone: '+91 9876543213'
  }
];

const topSalesStaff = [
  {
    name: 'Vikram Singh',
    id: 'SLS001',
    sales: 156,
    revenue: 1250000,
    target: 1500000,
    avatar: 'VS'
  },
  {
    name: 'Anita Desai',
    id: 'SLS002',
    sales: 142,
    revenue: 1180000,
    target: 1400000,
    avatar: 'AD'
  },
  {
    name: 'Rohit Gupta',
    id: 'SLS003',
    sales: 128,
    revenue: 980000,
    target: 1200000,
    avatar: 'RG'
  },
  {
    name: 'Kavita Jain',
    id: 'SLS004',
    sales: 115,
    revenue: 850000,
    target: 1100000,
    avatar: 'KJ'
  }
];

function RetailDashboard() {
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Delivered':
        return 'success';
      case 'Processing':
      case 'Pending':
        return 'warning';
      case 'Shipped':
        return 'info';
      default:
        return 'default';
    }
  };

  const getAgendaStatusColor = (status) => {
    switch (status) {
      case 'Confirmed':
      case 'Scheduled':
        return 'success';
      case 'Pending':
        return 'warning';
      case 'High Priority':
        return 'error';
      case 'In Progress':
        return 'info';
      default:
        return 'default';
    }
  };

  const getAgendaIcon = (type) => {
    switch (type) {
      case 'delivery':
        return <ShoppingCart />;
      case 'meeting':
        return <People />;
      case 'followup-customer':
        return <PersonAdd />;
      case 'inventory':
        return <Inventory />;
      case 'training':
        return <Schedule />;
      default:
        return <Schedule />;
    }
  };

  const getPendingTaskIcon = (type) => {
    switch (type) {
      case 'pending-delivery':
        return <EventBusy />;
      case 'followup':
        return <PendingActions />;
      case 'inventory-restock':
        return <Inventory />;
      default:
        return <Warning />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Critical':
        return 'error';
      case 'High':
        return 'warning';
      case 'Medium':
        return 'info';
      case 'Low':
        return 'success';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Retail Dashboard
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Welcome back! Here's your retail business overview for today.
        </Typography>
        <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
          {currentTime.toLocaleString('en-IN', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          })}
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {retailDashboardStats.map((stat, index) => {
          const IconComponent = stat.icon;
          return (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card elevation={3}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ bgcolor: stat.color, mr: 2 }}>
                      <IconComponent />
                    </Avatar>
                    <Box>
                      <Typography variant="h4" component="div" fontWeight="bold">
                        {stat.value}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {stat.title}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {stat.trending === 'up' ? (
                      <TrendingUp sx={{ color: 'success.main', mr: 1 }} />
                    ) : (
                      <TrendingDown sx={{ color: 'error.main', mr: 1 }} />
                    )}
                    <Typography
                      variant="body2"
                      color={stat.trending === 'up' ? 'success.main' : 'error.main'}
                      fontWeight="medium"
                    >
                      {stat.change} from last month
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Grid container spacing={3}>
        {/* Left Side - Recent Orders and Top Sales Staff */}
        <Grid item xs={12} lg={6}>
          {/* Recent Orders */}
          <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Recent Orders
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Order ID</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Customer</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Items</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Amount</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentOrders.map((order, index) => (
                    <TableRow
                      key={order.id}
                      sx={{
                        '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                        '&:hover': { backgroundColor: '#e3f2fd' }
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'primary.main' }}>
                          {order.id}
                        </Typography>
                      </TableCell>
                      <TableCell>{order.customerName}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'success.main' }}>
                          {order.items}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          {formatCurrency(order.amount)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={order.status}
                          color={getStatusColor(order.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="View Details">
                            <IconButton size="small" color="primary">
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Call Customer">
                            <IconButton size="small" color="success" href={`tel:${order.phone}`}>
                              <Phone />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Top Sales Staff */}
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Top Performing Sales Staff
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {topSalesStaff.map((staff, index) => {
                const progressPercentage = (staff.revenue / staff.target) * 100;
                return (
                  <Box
                    key={staff.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      p: 2,
                      border: '1px solid #e0e0e0',
                      borderRadius: 2,
                      backgroundColor: '#fafafa',
                      '&:hover': {
                        backgroundColor: '#f0f7ff',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      }
                    }}
                  >
                    <Box sx={{ position: 'relative', mr: 2 }}>
                      <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                        {staff.avatar}
                      </Avatar>
                      <Box
                        sx={{
                          position: 'absolute',
                          top: -4,
                          right: -4,
                          backgroundColor: progressPercentage >= 100 ? '#4caf50' : progressPercentage >= 75 ? '#ff9800' : '#2196f3',
                          color: 'white',
                          borderRadius: '50%',
                          width: 20,
                          height: 20,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.7rem',
                          fontWeight: 'bold'
                        }}
                      >
                        {index + 1}
                      </Box>
                    </Box>

                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.5 }}>
                        {staff.name}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <ShoppingCart sx={{ fontSize: 14 }} />
                          {staff.sales} sales
                        </Typography>
                        <Typography variant="caption" color="primary.main" sx={{ fontWeight: 'bold' }}>
                          {staff.id}
                        </Typography>
                      </Box>

                      <Box sx={{ mb: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="caption" color="textSecondary">
                            Target: {formatCurrency(staff.target)}
                          </Typography>
                          <Typography variant="caption" fontWeight="bold" color={progressPercentage >= 100 ? 'success.main' : 'primary.main'}>
                            {progressPercentage.toFixed(1)}%
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(progressPercentage, 100)}
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            backgroundColor: '#e0e0e0',
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: progressPercentage >= 100 ? '#4caf50' : progressPercentage >= 75 ? '#ff9800' : '#2196f3'
                            }
                          }}
                        />
                      </Box>
                    </Box>

                    <Box sx={{ textAlign: 'right', ml: 2 }}>
                      <Typography variant="h6" fontWeight="bold" color="success.main" sx={{ mb: 0.5 }}>
                        {formatCurrency(staff.revenue)}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Revenue
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Paper>
        </Grid>

        {/* Right Side - Today's Agenda and Pending Tasks */}
        <Grid item xs={12} lg={6}>
          {/* Today's Agenda */}
          <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Schedule /> Today's Agenda
            </Typography>
            <Typography variant="body2" color="primary" sx={{ mb: 3, fontWeight: 'bold' }}>
              {todaysAgenda.length} tasks scheduled for today
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {todaysAgenda.map((item) => (
                <Box
                  key={item.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 2,
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    backgroundColor: '#fafafa',
                    '&:hover': {
                      backgroundColor: '#f0f7ff',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: getAgendaStatusColor(item.status) === 'success' ? '#4caf50' :
                        getAgendaStatusColor(item.status) === 'warning' ? '#ff9800' :
                          getAgendaStatusColor(item.status) === 'error' ? '#f44336' : '#2196f3',
                      mr: 2,
                      width: 36,
                      height: 36
                    }}
                  >
                    {getAgendaIcon(item.type)}
                  </Avatar>

                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
                      {item.title}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="caption" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <People sx={{ fontSize: 14 }} />
                        {item.customer}
                      </Typography>
                      <Typography variant="caption" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <AccessTime sx={{ fontSize: 14 }} />
                        {item.time}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={item.status}
                      color={getAgendaStatusColor(item.status)}
                      size="small"
                      sx={{ fontSize: '0.7rem', height: 24 }}
                    />
                    <IconButton
                      size="small"
                      color="success"
                      href={`tel:${item.phone}`}
                      sx={{
                        backgroundColor: '#e8f5e8',
                        '&:hover': { backgroundColor: '#4caf50', color: 'white' }
                      }}
                    >
                      <Phone sx={{ fontSize: 16 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="primary"
                      sx={{
                        backgroundColor: '#e3f2fd',
                        '&:hover': { backgroundColor: '#2196f3', color: 'white' }
                      }}
                    >
                      <Flag sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </Box>
          </Paper>

          {/* Pending Tasks */}
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Warning color="warning" /> Pending Tasks
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Pending Deliveries */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: 2,
                  border: '1px solid #ff9800',
                  borderRadius: 2,
                  backgroundColor: '#fff8e1',
                  '&:hover': {
                    backgroundColor: '#ffecb3',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: '#ff9800',
                    mr: 2,
                    width: 40,
                    height: 40
                  }}
                >
                  <EventBusy />
                </Avatar>

                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.5 }}>
                    Pending Deliveries
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Orders that need to be delivered
                  </Typography>
                </Box>

                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="h4" fontWeight="bold" color="warning.main">
                    {pendingTasks.filter(task => task.type === 'pending-delivery').length}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Tasks
                  </Typography>
                </Box>
              </Box>

              {/* Follow-ups */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: 2,
                  border: '1px solid #2196f3',
                  borderRadius: 2,
                  backgroundColor: '#e3f2fd',
                  '&:hover': {
                    backgroundColor: '#bbdefb',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: '#2196f3',
                    mr: 2,
                    width: 40,
                    height: 40
                  }}
                >
                  <PendingActions />
                </Avatar>

                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.5 }}>
                    Follow-ups
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Customer follow-up calls and support
                  </Typography>
                </Box>

                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="h4" fontWeight="bold" color="primary.main">
                    {pendingTasks.filter(task => task.type === 'followup').length}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Tasks
                  </Typography>
                </Box>
              </Box>

              {/* Inventory Restock */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: 2,
                  border: '1px solid #f44336',
                  borderRadius: 2,
                  backgroundColor: '#ffebee',
                  '&:hover': {
                    backgroundColor: '#ffcdd2',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: '#f44336',
                    mr: 2,
                    width: 40,
                    height: 40
                  }}
                >
                  <Inventory />
                </Avatar>

                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.5 }}>
                    Inventory Restock
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Low stock alerts and restocking needs
                  </Typography>
                </Box>

                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="h4" fontWeight="bold" color="error.main">
                    {pendingTasks.filter(task => task.type === 'inventory-restock').length}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Tasks
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Total Summary */}
            <Box sx={{ mt: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 2, textAlign: 'center' }}>
              <Typography variant="body1" color="warning.main" sx={{ fontWeight: 'bold' }}>
                Total {pendingTasks.length} overdue tasks requiring immediate attention
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default RetailDashboard;